/**
 * V70 Phase 3-A: 게임대회 공통 설정 Firestore callable wrappers
 *
 * 이 모듈은 신규 BE 콜러블을 FE 에서 호출하기 위한 얇은 wrapper 입니다.
 * 기존 Mock serverApi (acidRainApi / marbleApi) 가 flag 분기로 이 wrapper 를 호출.
 *
 * 사용자 지시:
 *   - §1 "매 플레이 액션 전 BE 재검증" → checkTournamentGameAccess
 *   - §4 fail-closed → 조회 실패 시 throw (상위에서 catch 하여 UI 안내)
 */
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../../firebase/utils';

export type GameKey = 'acidRain' | 'marble';
export type PolicyMode = 'limited' | 'unlimited';

export interface GamePolicy {
  mode: PolicyMode;
  dailyLimit: number;
}

export interface GameSettingsResp {
  gameKey: GameKey;
  openStartAt: string | null;
  openEndAt: string | null;
  gracePeriodMinutes?: number;
  coinPolicy?: GamePolicy;
  dicePolicy?: GamePolicy;
  leaderboard: { epoch: number; lastResetAt: string | null; lastResetReason: string | null };
  updatedAt: string | null;
  updatedByName: string | null;
}

export interface PublicGameSettingsResp {
  gameKey: GameKey;
  enabled: boolean;
  audienceType: 'all' | 'kr' | 'cn' | 'specific';
  openStartAt: string | null;
  openEndAt: string | null;
  gracePeriodMinutes?: number;
  coinPolicy?: GamePolicy;
  dicePolicy?: GamePolicy;
  leaderboardEpoch: number;
  lastResetAt: string | null;
  lastResetReason: string | null;
}

export interface AccessCheckResp {
  allowed: boolean;
  reason?: 'disabled' | 'not_in_audience' | 'inactive' | 'before_open' | 'after_close' | 'allowed';
  message?: string;
  gameKey?: GameKey;
  leaderboardEpoch?: number;
  policy?: GamePolicy;
  gracePeriodMinutes?: number;
  openStartAt?: string;
  openEndAt?: string;
  bySystemAdmin?: boolean;
}

export interface HistoryItem {
  id: string;
  gameKey: GameKey;
  previousSettings: any;
  newSettings: any;
  changeType: string;
  changeReason: string | null;
  changedAt: string | null;
  changedBy: string | null;
  changedByName: string | null;
}

// 테스트 환경 (NODE_ENV='test') 에서는 BE 호출 skip — Mock 전제 테스트 호환.
const IS_TEST = process.env.NODE_ENV === 'test';

// 어드민 — 전체 조회
export async function callGetTournamentGameSettings(gameKey: GameKey): Promise<GameSettingsResp> {
  if (IS_TEST) {
    return { gameKey, openStartAt: null, openEndAt: null, leaderboard: { epoch: 0, lastResetAt: null, lastResetReason: null }, updatedAt: null, updatedByName: null };
  }
  const fn = httpsCallable<{ gameKey: GameKey }, { success: boolean; gameKey: GameKey; settings: GameSettingsResp }>(
    getFunctions(), 'getTournamentGameSettings'
  );
  const r = await fn({ gameKey });
  return r.data.settings;
}

// 어드민 — 오픈 기간 저장
export async function callUpdateTournamentOpenPeriod(
  gameKey: GameKey, openStartAt: string | null, openEndAt: string | null, gracePeriodMinutes?: number,
): Promise<void> {
  if (IS_TEST) return;
  const fn = httpsCallable<any, { success: boolean }>(getFunctions(), 'updateTournamentOpenPeriod');
  await fn({ gameKey, openStartAt, openEndAt, gracePeriodMinutes });
}

// 어드민 — 코인 정책 (acidRain)
export async function callUpdateTournamentCoinPolicy(policy: GamePolicy): Promise<void> {
  if (IS_TEST) return;
  const fn = httpsCallable<GamePolicy, { success: boolean }>(getFunctions(), 'updateTournamentCoinPolicy');
  await fn(policy);
}

// 어드민 — 주사위 정책 (marble)
export async function callUpdateTournamentDicePolicy(policy: GamePolicy): Promise<void> {
  if (IS_TEST) return;
  const fn = httpsCallable<GamePolicy, { success: boolean }>(getFunctions(), 'updateTournamentDicePolicy');
  await fn(policy);
}

// 어드민 — 리더보드 초기화
export async function callResetTournamentLeaderboard(gameKey: GameKey, reason: string): Promise<number> {
  if (IS_TEST) return 1;
  const fn = httpsCallable<{ gameKey: GameKey; reason: string }, { success: boolean; newEpoch: number }>(
    getFunctions(), 'resetTournamentLeaderboard'
  );
  const r = await fn({ gameKey, reason });
  return r.data.newEpoch;
}

// 어드민 — 변경 이력
export async function callGetTournamentGameSettingsHistory(gameKey: GameKey, limit: number = 10): Promise<HistoryItem[]> {
  const fn = httpsCallable<{ gameKey: GameKey; limit: number }, { success: boolean; items: HistoryItem[] }>(
    getFunctions(), 'getTournamentGameSettingsHistory'
  );
  const r = await fn({ gameKey, limit });
  return r.data.items;
}

// 사용자 — 공개 설정 조회
// 테스트 환경에서는 BE 호출 skip (Mock 전제 테스트 호환).
export async function callGetPublicTournamentGameSettings(gameKey: GameKey): Promise<PublicGameSettingsResp> {
  if (IS_TEST) {
    return {
      gameKey,
      enabled: true,
      audienceType: 'all',
      openStartAt: null,
      openEndAt: null,
      leaderboardEpoch: 1,
      lastResetAt: null,
      lastResetReason: null,
    };
  }
  const fn = httpsCallable<{ gameKey: GameKey }, PublicGameSettingsResp & { success: boolean }>(
    getFunctions(), 'getPublicTournamentGameSettings'
  );
  const r = await fn({ gameKey });
  return r.data;
}

// 사용자 — 매 플레이 액션 전 접근 검증 (fail-closed: throw 발생 가능)
export async function callCheckTournamentGameAccess(gameKey: GameKey): Promise<AccessCheckResp> {
  const fn = httpsCallable<{ gameKey: GameKey }, AccessCheckResp>(getFunctions(), 'checkTournamentGameAccess');
  const r = await fn({ gameKey });
  return r.data;
}

/**
 * 플레이 액션 전 사용 — allowed=false 면 Error throw.
 * fail-closed: callable 자체 실패 시에도 throw ("일시 오류").
 *
 * 테스트 환경 (NODE_ENV='test') 에서는 BE 호출 skip — Mock 전제 테스트 호환.
 */
export async function assertTournamentGameAccess(gameKey: GameKey): Promise<AccessCheckResp> {
  if (process.env.NODE_ENV === 'test') {
    return { allowed: true, reason: 'allowed', gameKey };
  }
  try {
    const r = await callCheckTournamentGameAccess(gameKey);
    if (!r.allowed) {
      throw new Error(r.message || '게임대회 접근 권한이 없습니다.');
    }
    return r;
  } catch (e: any) {
    // 네트워크/BE 오류 — fail-closed
    if (e?.message && !e.code) throw e;  // 이미 Error 로 throw 된 것 (allowed=false)
    throw new Error('설정 조회 실패. 네트워크를 확인하고 다시 시도해 주세요.');
  }
}
